import type { DrawerWidthDescriptor } from '@meterup/atto';
import {
  atContainers,
  colors,
  containerNames,
  containers,
  darkThemeSelector,
  DrawerWidthContext,
  styled,
  zIndexes,
} from '@meterup/atto';
import React, { useMemo, useState } from 'react';

import { Drawer } from './Drawer';

const StageGrid = styled('div', {
  display: 'grid',
  size: '100%',
  gridTemplateRows: 'min-content 1fr',
  gridTemplateAreas: '"controls controls controls" "skimmer pane drawer"',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
  },

  ...atContainers([containers.application.narrow], {
    strokeTop: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeTop: colors.strokeNeutralDark,
    },
  }),

  ...atContainers([containers.application.notNarrow], {
    strokeLeft: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeLeft: colors.strokeNeutralDark,
    },
  }),

  ...atContainers([containers.stage.narrow], {
    gridTemplateColumns: 'min-content 1fr 0',
  }),

  ...atContainers([containers.stage.notNarrow], {
    gridTemplateColumns: 'min-content 1fr min-content',
  }),
});
StageGrid.displayName = 'StageGrid';

const StageContainer = styled('div', {
  gridArea: 'stage',
  position: 'relative',
  zIndex: zIndexes.stage,
  containerType: 'inline-size',
  containerName: containerNames.stage,
  size: '100%',
});
StageContainer.displayName = 'StageContainer';

export function Stage({
  breadcrumb,
  controls,
  skimmer,
  pane,
  drawer,
  drawerOpen = false,
}: {
  breadcrumb?: React.ReactNode;
  controls?: React.ReactNode;
  skimmer?: React.ReactNode;
  pane: React.ReactNode;
  drawer?: React.ReactNode;
  drawerOpen?: boolean;
}) {
  const [drawerWidthDescriptor, setDrawerWidthDescriptor] = useState<DrawerWidthDescriptor | null>(
    null,
  );
  const drawerWidthContextValue = useMemo(
    () => ({
      descriptor: drawerWidthDescriptor,
      setDescriptor: setDrawerWidthDescriptor,
    }),
    [drawerWidthDescriptor],
  );

  return (
    <StageContainer>
      <DrawerWidthContext.Provider value={drawerWidthContextValue}>
        <StageGrid>
          {controls}
          {breadcrumb}
          {skimmer}
          {pane}
          <Drawer open={drawerOpen}>{drawer}</Drawer>
        </StageGrid>
      </DrawerWidthContext.Provider>
    </StageContainer>
  );
}
